/**
 * „Woher die Anfos kommen" (Deck 23.09., Folie 4; Umbau nach dem KPI-Review, vorschlag_v3 §2.2): je Kanalgruppe,
 * aufklappbar bis zum Kanal — Anfos, Anteil, Erwarteter Wert (Summe · Ø), Anteil Erstbesteller, Bezahlquote und die
 * Veränderung gegen die gewählte Basis (Till 23.10.2026, E6/E9: 7 Spalten im Standard). Weitere Spalten über die
 * Spaltenwahl; Kanal- und Blog-Karte teilen Liste und gespeicherte Wahl (`kennzahlSpalten`).
 * Backend: /api/auswertungen/kanaele/kanaele (kanaeleService.kanaele).
 *
 * Persona-Umbau D3 (28.10.2026): Befundzeile zu den Direkt-Mailings (Menge und Quote getrennt, aus `dmHaelften`) und die
 * Marke „Kennung geändert" nur an den Zeilen, deren Kennung sich geändert hat (`befund.KENNUNG_GEAENDERT`) — bis dahin stand
 * der Hinweis pauschal unter der Tabelle.
 */
import { FC, useMemo } from 'react';
import { Box, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ChartCard from '../../../components/auswertungen/ChartCard';
import InfoTip from '../../../components/InfoTip';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import { useVizStyle } from '../../../components/auswertungen/vizStyle';
import { BefundZeile } from '../../../components/auswertungen/kennzahlTeile';
import { ATTRIBUTION, GLOSSAR, TERMS } from '../../../helpers/kpiDefinitions';
import { KanaeleAntwort, KanalGruppeKey, KanalKennzahlen, VergleichsBasis } from '../../../helpers/auswertungenData';
import { AbweichungMarke, Hinweis, int, Laden, vorlaeufigSatz } from '../webinare-produkte/shared';
import {
  basisBegriff,
  basisFenster,
  Etikett,
  ENG_UNTER,
  Farbfeld,
  KANAL_STANDARDS,
  fensterSatz,
  jetztSpanne,
  kennzahlSpalten,
  nachGruppen,
  useBreitePx,
} from './shared';
import { dmKanalBefund, kennungGeaendert } from './befund';
import type { DmHaelften } from './personaDaten';

/** Eine Tabellenzeile: Gruppe (Ebene 0) oder Kanal (Ebene 1, Kind seiner Gruppe). */
interface KanalZeile extends KanalKennzahlen {
  id: string;
  label: string;
  description: string;
  gruppe: KanalGruppeKey;
  ebene: 0 | 1;
  /** Sichtbare Kanäle der Gruppe (mit Anfos im Zeitraum). */
  kanaele?: KanalZeile[];
  /** Gruppe hat mehr als einen Kanal (auch ohne Anfos im Zeitraum) — dann aufklappbar, selbst mit nur einem sichtbaren. */
  teilbar?: boolean;
  /** Grund der Marke „Kennung geändert" (Kanal selbst bzw. einer der Kanäle der Gruppe, auch ohne Anfos), sonst null. */
  kennung: string | null;
}

const KanalKarte: FC<{ data: KanaeleAntwort | null; fehler: string | null; basis: VergleichsBasis; haelften: DmHaelften | null }> = ({
  data,
  fehler,
  basis,
  haelften,
}) => {
  const viz = useVizStyle();

  // Zeilen mit 0 Anfos liefert das Backend mit, wenn sie im Vergleichsfenster Anfos hatten (ihre Veränderung steckt in
  // der Gruppe) — angezeigt werden sie nur in der Fußnote (vorschlag_v3 §2.0/7).
  const { zeilen, ohneAnfos } = useMemo(() => {
    const ohne: string[] = [];
    const z: KanalZeile[] = [];
    for (const g of data?.groups ?? []) {
      const kanaele: KanalZeile[] = [];
      for (const c of g.channels) {
        if (c.anfos > 0) kanaele.push({ ...c, id: `c:${c.key}`, gruppe: g.key, ebene: 1, kennung: kennungGeaendert([c.key]) });
        else ohne.push(c.label);
      }
      if (g.anfos > 0) {
        // Nur die Kennzahlen der Gruppe übernehmen (Typ), Name und Kanäle setzt die Zeile selbst.
        const kennzahlen: KanalKennzahlen = g;
        z.push({
          ...kennzahlen,
          id: `g:${g.key}`,
          label: g.label,
          description: g.description,
          gruppe: g.key,
          ebene: 0,
          kanaele,
          teilbar: g.channels.length > 1,
          kennung: kennungGeaendert(g.channels.map((c) => c.key)),
        });
      }
    }
    return { zeilen: z, ohneAnfos: ohne };
  }, [data]);

  const schwellen = data?.grau_schwellen ?? { bezahlquote_reife_anfos: 20, anteil_anfos: 20, veraenderung_anfos: 20, markierung_reife_anfos: 20 };
  // `schmalSpalte`: Kartenbreite unter 640 px (Stufe aus der Spaltenwahl) — die Namensspalte wird schmaler.
  const baue = (sichtbar: ReadonlySet<string>, schmalSpalte = false): Spalte<KanalZeile>[] => [
    {
      key: 'name',
      label: 'Kanal',
      tip: `${TERMS.kanal.tip} ${TERMS.kanalGruppe.tip}`,
      gruppe: 'Menge',
      pflicht: true,
      // Am Handy (390 px) stand die Tabelle mit 150 px Namensspalte 21 px über (gemessen 23.10.).
      minWidth: schmalSpalte ? 96 : 150,
      render: (r, k) => (
        <Box component="span" sx={{ fontWeight: r.ebene === 0 ? 600 : 400, color: r.ebene === 0 ? 'inherit' : 'text.secondary', lineHeight: 1.3 }}>
          {r.ebene === 0 && <Farbfeld color={viz.kanalFarben[r.gruppe]} />}
          {r.label}
          <InfoTip text={r.gruppe === 'other' && r.ebene === 0 ? `${r.description} ${GLOSSAR.kennung}.` : r.description} />
          {/* ▲/▼ wandert an den Namen, wenn die Bezahlquote ausgeblendet ist (vorschlag_v3 §5.4). */}
          {!sichtbar.has('quote') && <AbweichungMarke marke={r.pay_rate_flag} />}
          {k.sortGrund && <Etikett>{k.sortGrund}</Etikett>}
          {/* D3: die Warnung nur an Zeilen mit geänderter Kennung, und nur wenn die Veränderung zu sehen ist. */}
          {r.kennung && sichtbar.has('veraenderung') && (
            <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
              <Etikett>{TERMS.umbuchungMoeglich.short}</Etikett>
              <InfoTip text={`${r.kennung} ${TERMS.umbuchungMoeglich.tip}`} />
            </Box>
          )}
        </Box>
      ),
    },
    ...kennzahlSpalten<KanalZeile>({
      basis,
      schwellen,
      reifeTage: data?.reife_tage ?? 60,
      total: data?.total ?? null,
      farbe: (r) => viz.kanalFarben[r.gruppe],
      sichtbar,
    }),
  ];
  const meta = nachGruppen(spaltenMeta(baue(new Set())));
  const wahl = useSpaltenwahl('kanal', meta, KANAL_STANDARDS);
  const [breiteRef, breitePx] = useBreitePx(wahl.messRef);
  const sichtbar = new Set(wahl.sichtbar);
  const spalten = baue(sichtbar, wahl.breite === 'schmal').filter((s) => sichtbar.has(s.key));
  const schmal = wahl.breite === 'schmal';
  const begriff = basisBegriff(basis);
  const dm = data?.groups.find((g) => g.key === 'direct_mailing');
  const befund = data
    ? dmKanalBefund(dm?.veraenderung[basis], basisFenster(data.vergleich, basis), haelften, schwellen.veraenderung_anfos)
    : null;
  const mitKennung = zeilen.some((r) => r.kennung || r.kanaele?.some((c) => c.kennung));

  return (
    <ChartCard
      title="Woher die Anfos kommen"
      actions={<SpaltenMenue spalten={meta} wahl={wahl} />}
      howToRead={
        <>
          <Box component="span" sx={{ fontWeight: 600 }}>
            Je Zeile ein Weg, über den bestellt wurde:
          </Box>{' '}
          wie viele Anfos er bringt, welchen erwarteten Wert, wie viele davon von neuen Bestellern kommen und wie sich der Wert gegenüber dem
          Vergleichszeitraum verändert hat. Gruppen aufklappen zeigt die Kanäle. Webinar-Käufe stehen bei Webinar, auch wenn die Anmeldung über
          einen Newsletter kam; welche Einladung sie gebracht hat, zeigt die Karte „Webinar-Einladungen“. Zahlt ein Kanal auffällig besser oder
          schlechter, steht ▲/▼ an seiner Bezahlquote (ist die Spalte aus, am Namen). Nur ▲/▼ sind auffällig.
        </>
      }
      terms={[
        { label: ATTRIBUTION.bannerKanal.label, tip: ATTRIBUTION.bannerKanal.tip },
        { label: TERMS.kanalGruppe.label, tip: TERMS.kanalGruppe.tip },
        { label: TERMS.wertBeiBestellung.label, tip: TERMS.wertBeiBestellung.tip },
        { label: TERMS.anteilErstbesteller.label, tip: TERMS.anteilErstbesteller.tip },
        { label: begriff.label, tip: begriff.tip },
        { label: TERMS.abweichungMarke.label, tip: TERMS.abweichungMarke.tip },
        { label: TERMS.umbuchungMoeglich.label, tip: TERMS.umbuchungMoeglich.tip },
        { label: TERMS.direktMailingHaelften.label, tip: TERMS.direktMailingHaelften.tip },
        ...(data && data.total.provisional > 0 ? [{ label: TERMS.vorlaeufig.label, tip: TERMS.vorlaeufig.tip }] : []),
      ]}
      source="PL-X-Bestellungen und Zahlungen, Kennung im Bestell-Link"
      freshnessKey={['plx_orders', 'anfo_werte']}
    >
      <Box ref={breiteRef}>
        {!data ? (
          <Laden fehler={fehler} />
        ) : (
          <>
            {befund && (
              <BefundZeile
                text={'text' in befund ? befund.text : undefined}
                fehlt={'fehlt' in befund ? befund.fehlt : null}
                tip={`${begriff.tip} ${TERMS.direktMailingHaelften.tip}`}
              />
            )}
            <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Gezählt wird bis zum Datenstand, nicht bis zum exklusiven Fensterende (B50/B74). */}
              Bestellungen {jetztSpanne(data.vergleich)} · Veränderung gegen {basisFenster(data.vergleich, basis)}
              <InfoTip text={fensterSatz(data.vergleich, basis)} />
            </Box>
            <SortableTable
              rows={zeilen}
              columns={spalten}
              rowKey={(r) => r.id}
              initialSort={{ key: 'anfos', dir: 'desc' }}
              // Auch mit nur EINEM sichtbaren Kanal aufklappbar, wenn die Gruppe weitere (ohne Anfos) enthält: deren
              // Vergleichswerte stecken in der Gruppe, der sichtbare Kanal hat eine eigene Veränderung (z. B. „neu").
              kinder={(r) => (r.kanaele && r.kanaele.length > 0 && (r.kanaele.length > 1 || r.teilbar) ? r.kanaele : undefined)}
              // „Sonstige und ohne Kennung" steht immer unten, ohne eigene Überschrift (vorschlag_v3 §2.2).
              abschnitt={(r) => (r.ebene === 0 && r.gruppe === 'other' ? '' : null)}
              summaryLabel="Alle Anfos"
              eng={schmal || (breitePx !== null && breitePx < ENG_UNTER)}
              festeErsteSpalte
            />
            {sichtbar.has('veraenderung') && mitKennung && (
              // E9 (Till 23.10.2026) sichtbar unter der Tabelle; seit D3 (28.10.2026) nur noch als Verweis auf die Marke der Zeilen.
              <Hinweis>
                „{TERMS.umbuchungMoeglich.short}“ steht an Zeilen, deren Kennung in den Bestell-Links sich geändert hat — dort kann ein Teil der
                Veränderung Umbuchung sein. Die Summe aller Anfos betrifft das nicht.
              </Hinweis>
            )}
            <Hinweis>
              Euro netto. — = nicht messbar; der Grund steht im Tooltip. Kundentypen gibt es auch je Quartal:{' '}
              <Link component={RouterLink} to="/auswertungen/umsatz" underline="hover">
                Umsatz &amp; Bezahlqualität → Wer bestellt — und wer zahlt
              </Link>
              .{ohneAnfos.length > 0 && ` Ohne Anfos im Zeitraum: ${ohneAnfos.join(', ')}.`} {vorlaeufigSatz(data.total.provisional)}
              {data.total.provisional > 0 &&
                ` Davon: ${data.groups
                  .filter((g) => g.provisional > 0)
                  .map((g) => `${g.label} ${int(g.provisional)}`)
                  .join(', ')}.`}
            </Hinweis>
          </>
        )}
      </Box>
    </ChartCard>
  );
};

export default KanalKarte;
